import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  MdAdminPanelSettings,
  MdOutlineGroups,
  MdOutlineGroups2,
  MdOutlinePaid,
  MdOutlineVerifiedUser,
} from 'react-icons/md';
import { CiHome } from 'react-icons/ci';
import { SidebarFooter } from './SidebarFooter';
import { Menu, Transition } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChartIcon } from 'lucide-react';
import { FaCcVisa } from 'react-icons/fa';
import { File } from 'react-feather';

const adminNavItems = [
  { to: '/app', label: 'Home', icon: <CiHome size={22} /> },
  {
    label: 'Users',
    icon: <MdOutlineGroups2 size={22} />,
    children: [
      { to: '/app/users', label: 'User Listings' },
      { to: '/app/users/vaults', label: 'User Vaults' },
      {
        to: '/app/users/vault-transfer-settings',
        label: 'Vault Transfer Settings',
      },
    ],
  },
  {
    to: '/app/administration',
    label: 'Administration',
    icon: <MdAdminPanelSettings size={22} />,
  },
  {
    to: '/app/finance',
    label: 'Finance',
    icon: <MdOutlinePaid size={22} />,
  },
  {
    to: '/app/analytics',
    label: 'Analytics',
    icon: <LineChartIcon size={22} />,
  },
  {
    to: '/app/transactions',
    label: 'Transactions',
    icon: <MdOutlineVerifiedUser size={22} />,
  },
  {
    to: '/app/kyc',
    label: 'KYC Console',
    icon: <MdOutlineGroups size={22} />,
  },
];

const developerNavItems = [
  { to: '/app/dev/overview', label: 'Overview', icon: <CiHome size={22} /> },
  {
    to: '/app/dev/projects',
    label: 'Projects',
    icon: <MdOutlineGroups size={22} />,
  },
  { to: '/app/dev/tasks', label: 'Tasks', icon: <MdOutlinePaid size={22} /> },
  {
    to: '/app/dev/timeline',
    label: 'Timeline',
    icon: <MdOutlineVerifiedUser size={22} />,
  },
  {
    to: '/app/dev/notes',
    label: 'Notes',
    icon: <MdOutlineGroups2 size={22} />,
  },
  {
    to: '/app/dev/subscriptions',
    label: 'Subscriptions',
    icon: <MdAdminPanelSettings size={22} />,
  },
  {
    to: '/app/dev/changelog',
    label: 'Changelog',
    icon: <MdOutlineVerifiedUser size={22} />,
  },
];

const vaultNavItems = [
  {
    label: 'Structure',
    children: [
      { to: '/vault/overview', label: 'Overview' }, // ← Injected
      { to: '/vault/access', label: 'Access Control' },
      { to: '/vault/master', label: 'Master Vault' },
      { to: '/vault/branches', label: 'Branch Vaults' },
    ],
  },
  {
    label: 'Float Movement',
    children: [
      { to: '/vault/float-allocation', label: 'Float Allocation' },
      { to: '/vault/replenishment', label: 'Auto-Replenishment' },
    ],
  },
  {
    label: 'Incident Management',
    children: [
      { to: '/vault/incidents', label: 'View Incidents' },
      { to: '/vault/incident-settings', label: 'Incident Settings' },
    ],
  },
];

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mode, setMode] = useState(Cookies.get('admin_mode') || 'admin');
  const [showModeMenu, setShowModeMenu] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  const toggleSection = (label) => {
    setOpenSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    Cookies.set('admin_mode', newMode, { expires: 365 });
    setShowModeMenu(false);

    // Auto redirect
    if (newMode === 'developer') navigate('/app/dev/overview');
    else if (newMode === 'vault') navigate('/vault/overview');
    else navigate('/app');
  };

  const navItems =
    mode === 'developer'
      ? developerNavItems
      : mode === 'vault'
      ? vaultNavItems
      : adminNavItems;

  const isLinkActive = (to) => location.pathname === to;

  return (
    <aside
      className={`
        h-screen sticky top-0 z-20 flex flex-col justify-between border-r transition-all duration-300 flex-shrink-0 min-w-[5rem]
        ${collapsed ? 'w-20' : 'w-64'}
        ${
          mode === 'developer'
            ? 'bg-[#273e1a] border-[#273e1a]/10 text-indigo-100'
            : mode === 'admin'
            ? 'backdrop-blur-md bg-white/10 dark:bg-white/5 border-white/10 text-white'
            : 'bg-[#430c3b] border-[#430c3b]/10 text-white'
        }
      `}
    >
      {/* Header */}
      <div className="relative px-4 py-4 flex justify-between items-center">
        <div className="text-lg font-bold tracking-tight cursor-pointer select-none">
          {!collapsed &&
            (mode === 'admin'
              ? 'Overwatch'
              : mode === 'developer'
              ? 'Wirelab'
              : 'Vault Layer')}
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 hover:bg-white/10 rounded transition"
        >
          {collapsed ? '»' : '«'}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 space-y-1">
        {navItems.map((item) =>
          item.children ? (
            <div key={item.label} className="space-y-1">
              <button
                onClick={() => toggleSection(item.label)}
                className={`
                  w-full flex items-center justify-between gap-3 px-4 py-2 rounded-lg transition-all
                  ${
                    mode === 'developer'
                      ? 'hover:bg-[#335222]'
                      : 'hover:bg-white/10'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <span className="flex-shrink-0">{item.icon}</span>
                  {!collapsed && <span>{item.label}</span>}
                </div>
                {!collapsed && (
                  <span>{openSections[item.label] ? '−' : '+'}</span>
                )}
              </button>

              <AnimatePresence initial={false}>
                {openSections[item.label] && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="ml-8 space-y-1 overflow-hidden"
                  >
                    {item.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        className={`
                          block px-4 py-1.5 rounded-md text-sm transition-all
                          ${
                            isLinkActive(child.to)
                              ? 'bg-white/10 font-semibold'
                              : mode === 'developer'
                              ? 'text-indigo-200 hover:bg-[#335222]'
                              : 'text-white/70 hover:bg-white/10'
                          }
                        `}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <Link
              key={item.to}
              to={item.to}
              className={`
                flex items-center gap-3 px-4 py-2 rounded-lg transition-all
                ${
                  isLinkActive(item.to)
                    ? mode === 'developer'
                      ? 'bg-[#335222] font-semibold text-indigo-100'
                      : 'bg-white/10 font-semibold text-white'
                    : mode === 'developer'
                    ? 'text-indigo-200 hover:bg-[#335222]'
                    : 'text-white/70 hover:bg-white/10'
                }
              `}
            >
              <span className="flex-shrink-0">{item.icon}</span>
              <span
                className={`${
                  collapsed ? 'opacity-0 w-0' : 'opacity-100 w-auto'
                } transition-opacity duration-300`}
              >
                {item.label}
              </span>
            </Link>
          )
        )}
      </nav>

      {/* Footer */}
      <SidebarFooter collapsed={collapsed} />
    </aside>
  );
};

export { Sidebar };
